import { DateRangePicker, DateRangePickerProps, FormField, SegmentedControl, SpaceBetween } from '@cloudscape-design/components';
import { addDays, addMonths, addWeeks, addYears, endOfMonth, endOfWeek, endOfYear, format, parse, startOfMonth, startOfWeek, startOfYear } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { normalizeDate } from '../pages/commons/functions';

interface DRPickerModalModalProps {
    start?: string;
    end?: string;
    clear?: boolean;
    onConfirm: (startDate: string, endDate: string) => void;
}

const DRPickerModal: React.FC<DRPickerModalModalProps> = ({
    start = format(new Date(), 'dd-MM-yyyy'),
    end = format(new Date(), 'dd-MM-yyyy'),
    clear = true,
    onConfirm,
}) => {
    if (start === "" && end === "") {
        start = format(new Date(), 'dd-MM-yyyy');
        end = format(new Date(), 'dd-MM-yyyy');
    }
    const [selectedRelativeRange, setSelectedRelativeRange] = useState<string>('');
    const [date, setDate] = useState<DateRangePickerProps.Value | null>({
        type: "absolute",
        startDate: format(parse(start, "dd-MM-yyyy", new Date()), "yyyy-MM-dd"),
        endDate: format(parse(end, "dd-MM-yyyy", new Date()), "yyyy-MM-dd")
    });

    useEffect(() => {
        if (start && end && (start !== "" || end !== "")) {
            setDate({
                type: "absolute",
                startDate: format(parse(start, "dd-MM-yyyy", new Date()), "yyyy-MM-dd"),
                endDate: format(parse(end, "dd-MM-yyyy", new Date()), "yyyy-MM-dd"),
            });
        }
    }, [start, end]);

    {/*
    const selectedRangeOptions2 = [
        { label: "Hoy", group: "days", value: "today" },
        { label: "Ayer", group: "days", value: "yesterday" },
        { label: "Esta semana", group: "days", value: "thisweek" },
        { label: "Semana pasada", group: "days", value: "pastweek" },
        { label: "Este mes", group: "months", value: "thismonth" },
        { label: "Mes pasado", group: "months", value: "previousmonth" },
        { label: "Últ 3 meses", group: "months", value: "last3months" },
        { label: "Últ 6 meses", group: "months", value: "last6months" },
        { label: "Este año", group: "years", value: "thisyear" },
        { label: "Año pasado", group: "years", value: "lastyear" },
        { label: "Año antepasado", group: "years", value: "last2years" },
        { label: "1 año", group: "since", value: "1year" },
        { label: "2 años", group: "since", value: "2years" },
        { label: "3 años", group: "since", value: "3years" },
    ];
    */}

    const selectedRangeOptions = [
        { text: "Hoy", group: "days", id: "today" },
        { text: "Ayer", group: "days", id: "yesterday" },
        { text: "Esta", group: "days", id: "thisweek" },
        { text: "Pasada", group: "days", id: "pastweek" },
        { text: "Este", group: "months", id: "thismonth" },
        { text: "Pasado", group: "months", id: "previousmonth" },
        { text: "Últ 3", group: "months", id: "last3months" },
        { text: "Últ 6", group: "months", id: "last6months" },
        { text: "Este", group: "years", id: "thisyear" },
        { text: "Pasado", group: "years", id: "lastyear" },
        { text: "Antepasado", group: "years", id: "last2years" },
        { text: "1 año", group: "since", id: "1year" },
        { text: "2 años", group: "since", id: "2years" },
        { text: "3 años", group: "since", id: "3years" },
    ];
    const calculateDates = (rangeKey: string) => {
        const today = new Date();
        let startDate = "";
        let endDate = "";

        switch (rangeKey) {
            case "today":
                startDate = format(today, 'dd-MM-yyyy');
                endDate = format(today, 'dd-MM-yyyy');
                break;
            case "yesterday":
                startDate = format(addDays(today, -1), 'dd-MM-yyyy');
                endDate = format(addDays(today, -1), 'dd-MM-yyyy');
                break;
            case "thisweek":
                startDate = format(startOfWeek(today, { weekStartsOn: 1 }), 'dd-MM-yyyy');
                endDate = format(endOfWeek(today, { weekStartsOn: 1 }), 'dd-MM-yyyy');
                break;
            case "pastweek":
                startDate = format(addWeeks(startOfWeek(today, { weekStartsOn: 1 }), -1), 'dd-MM-yyyy');
                endDate = format(addDays(startOfWeek(today, { weekStartsOn: 1 }), -1), 'dd-MM-yyyy');
                break;
            case "thismonth":
                startDate = format(startOfMonth(today), 'dd-MM-yyyy');
                endDate = format(endOfMonth(today), 'dd-MM-yyyy');
                break;
            case "previousmonth":
                startDate = format(startOfMonth(addMonths(today, -1)), 'dd-MM-yyyy');
                endDate = format(endOfMonth(addMonths(today, -1)), 'dd-MM-yyyy');
                break;
            case "last3months":
                endDate = format(today, 'dd-MM-yyyy');
                startDate = format(addMonths(today, -3), 'dd-MM-yyyy');
                break;
            case "last6months":
                endDate = format(today, 'dd-MM-yyyy');
                startDate = format(addMonths(today, -6), 'dd-MM-yyyy');
                break;
            case "thisyear":
                startDate = format(startOfYear(today), 'dd-MM-yyyy');
                endDate = format(today, 'dd-MM-yyyy');
                break;
            case "lastyear":
                startDate = format(startOfYear(addYears(today, -1)), 'dd-MM-yyyy');
                endDate = format(endOfYear(addYears(today, -1)), 'dd-MM-yyyy');
                break;
            case "last2years":
                startDate = format(startOfYear(addYears(today, -2)), 'dd-MM-yyyy');
                endDate = format(endOfYear(addYears(today, -2)), 'dd-MM-yyyy');
                break;
            case "1year":
                startDate = format(addYears(today, -1), 'dd-MM-yyyy');
                endDate = format(today, 'dd-MM-yyyy');
                break;
            case "2years":
                startDate = format(addYears(today, -2), 'dd-MM-yyyy');
                endDate = format(today, 'dd-MM-yyyy');
                break;
            case "3years":
                startDate = format(addYears(today, -3), 'dd-MM-yyyy');
                endDate = format(today, 'dd-MM-yyyy');
                break;
        }

        return { startDate, endDate };
    };

    const handleRadioChange = (value: string) => {
        setSelectedRelativeRange(value);
        return;
        const { startDate, endDate } = calculateDates(value);
        if (startDate && endDate) {
            const formattedStart = format(parse(startDate, "dd-MM-yyyy", new Date()), "yyyy-MM-dd");
            const formattedEnd = format(parse(endDate, "dd-MM-yyyy", new Date()), "yyyy-MM-dd");
            // Update component's internal range value immediately so the UI reflects it
            setDate({
                type: "absolute",
                startDate: formattedStart,
                endDate: formattedEnd,
            });
        } else {

        }
    };
    return (
        <SpaceBetween direction="vertical" size="xs">
            <div style={{ width: "230px" }}>
                <DateRangePicker
                    relativeOptions={[]}
                    hideTimeOffset
                    expandToViewport
                    onChange={({ detail }) => {

                        if (detail.value === null) {
                            if (!selectedRelativeRange) {
                                setDate(null);
                                onConfirm("", "");
                                console.log("TEST1")
                            } else {
                                //const absData = detail.value as DateRangePickerProps.AbsoluteValue;
                                const { startDate, endDate } = calculateDates(selectedRelativeRange);
                                onConfirm(startDate, endDate);
                            }
                        } else if (detail.value.type === "absolute") {

                            const absData = detail.value as DateRangePickerProps.AbsoluteValue;
                            onConfirm(normalizeDate(absData.startDate), normalizeDate(absData.endDate));
                            setDate({
                                type: "absolute",
                                startDate: format(new Date(absData.startDate), "yyyy-MM-dd"),
                                endDate: format(new Date(absData.endDate), "yyyy-MM-dd"),
                            });
                            console.log("TEST3")
                        }
                        setSelectedRelativeRange('');
                    }}
                    value={date}
                    renderRelativeRangeContent={() => (
                        <SpaceBetween size="xs" direction="vertical">
                            <FormField label="Días/Semanas">
                                <SegmentedControl options={selectedRangeOptions.filter(option => option.group === "days")}
                                    selectedId={selectedRelativeRange} onChange={({ detail }) => handleRadioChange(detail.selectedId)} />


                                {/** 
                                 * 
                                 <SegmentedControl options={selectedRangeOptions.filter(option => option.group === "days")}
                                    selectedId={selectedRelativeRange} onChange={({ detail }) => handleRadioChange(detail.selectedId)} />
                                    
                                <RadioGroup
                                    value={selectedRelativeRange}
                                    onChange={(e) => handleRadioChange(e.detail.value)}
                                    direction='horizontal'
                                    items={selectedRangeOptions.filter(option => option.group === "days")}
                                />
                            */}
                            </FormField>
                            <FormField label="Meses">
                                <SegmentedControl options={selectedRangeOptions.filter(option => option.group === "months")}
                                    selectedId={selectedRelativeRange} onChange={({ detail }) => handleRadioChange(detail.selectedId)} />


                                {/** 
                               <SegmentedControl options={selectedRangeOptions.filter(option => option.group === "months")}
                                    selectedId={selectedRelativeRange} onChange={({ detail }) => handleRadioChange(detail.selectedId)} />

                               <RadioGroup
                                    value={selectedRelativeRange}
                                    onChange={(e) => handleRadioChange(e.detail.value)}
                                    direction='horizontal'
                                    items={selectedRangeOptions.filter(option => option.group === "months")}
                                />
                            */}
                            </FormField>
                            <FormField label="Años">
                                <SegmentedControl options={selectedRangeOptions.filter(option => option.group === "years")}
                                    selectedId={selectedRelativeRange} onChange={({ detail }) => handleRadioChange(detail.selectedId)} />
                                {/**
                               
                                <SegmentedControl options={selectedRangeOptions.filter(option => option.group === "years")}
                                    selectedId={selectedRelativeRange} onChange={({ detail }) => handleRadioChange(detail.selectedId)} />
                               <RadioGroup
                                    value={selectedRelativeRange}
                                    onChange={(e) => handleRadioChange(e.detail.value)}
                                    direction='horizontal'
                                    items={selectedRangeOptions.filter(option => option.group === "years")}
                                />
                                 */}
                            </FormField>
                            <FormField label="Hace">
                                <SegmentedControl options={selectedRangeOptions.filter(option => option.group === "since")}
                                    selectedId={selectedRelativeRange} onChange={({ detail }) => handleRadioChange(detail.selectedId)} />

                                {/** 
                                
                                <SegmentedControl options={selectedRangeOptions.filter(option => option.group === "since")}
                                    selectedId={selectedRelativeRange} onChange={({ detail }) => handleRadioChange(detail.selectedId)} />
                                
                                <RadioGroup
                                    value={selectedRelativeRange}
                                    onChange={(e) => handleRadioChange(e.detail.value)}
                                    direction='horizontal'
                                    items={selectedRangeOptions.filter(option => option.group === "since")}
                                />*/}
                            </FormField>
                        </SpaceBetween>
                    )}
                    onFocus={() => {
                        //NADA
                    }}

                    onBlur={() =>
                        setSelectedRelativeRange('')
                    }
                    isValidRange={range => {
                        if (range && range.type === "absolute") {
                            const [startDateWithoutTime] = range.startDate.split("T");
                            const [endDateWithoutTime] = range.endDate.split("T");
                            if (!startDateWithoutTime || !endDateWithoutTime) {
                                return {
                                    valid: false,
                                    errorMessage: "El rango es inválido. Seleccione inicio y término para el rango de fechas."
                                };
                            }
                            if (new Date(range.startDate).getTime() - new Date(range.endDate).getTime() > 0) {
                                return {
                                    valid: false,
                                    errorMessage: "El rango es inválido. Inicio debe ser anterior al término."
                                };
                            }
                        }
                        return { valid: true };
                    }}
                    locale='es-CL'
                    i18nStrings={{
                        absoluteModeTitle: "Absoluto",
                        applyButtonLabel: "Seleccionar",
                        cancelButtonLabel: "Cancelar",
                        relativeModeTitle: "Relativo",
                        todayAriaLabel: "Hoy",
                        customRelativeRangeDurationLabel: "Duración",
                        customRelativeRangeDurationPlaceholder: "Ingrese duración",
                        customRelativeRangeOptionDescription: "Rango customizado",
                        customRelativeRangeOptionLabel: "Manual",
                        customRelativeRangeUnitLabel: "Intervalo",
                        clearButtonLabel: "Borrar",
                    }}
                    dateInputFormat="iso"
                    dateOnly
                    startOfWeek={1}
                    showClearButton={clear}
                    placeholder="Seleccionar rango"
                />
            </div>
        </SpaceBetween>
    );
};

export default DRPickerModal;